import {Injectable} from '@angular/core';
import {isEqual} from 'lodash';
import {distinctUntilChanged, Observable} from 'rxjs';
import {filter, map, tap} from 'rxjs/operators';
import {CheckIbanCommand} from 'src/app/domain/command/custodian.command';
import {ERROR_CODES} from 'src/app/domain/error/error-codes';
import ValidationError from 'src/app/domain/error/validation-error';
import {objKeysToSnakeCase} from 'src/app/domain/function/object.helper';
import {ApiClient} from 'src/app/infrastructure/http/api.client';
import {HttpResponse, isNoContent} from 'src/app/infrastructure/http/http-response';
import {PortfolioAccount} from 'src/app/domain/model/portfolio/portfolio-account.model';
import PortfolioAccountStore from 'src/app/domain/store/portfolio-account.store';

@Injectable({
  providedIn: 'root',
})
export default class PortfolioAccountApi {
  private fetchAllInFlight: Promise<void> | null = null;
  private hasRequested = false;
  private generation = 0;

  constructor(private http: ApiClient, private store: PortfolioAccountStore) {
  }

  public fetchAll(): Promise<void> {
    this.hasRequested = true;

    if (!this.fetchAllInFlight) {
      const request: Promise<void> = this.requestAll().finally(() => {
        if (this.fetchAllInFlight === request) {
          this.fetchAllInFlight = null;
        }
      });
      this.fetchAllInFlight = request;
    }

    return this.fetchAllInFlight;
  }

  public refresh(): Promise<void> {
    this.generation++;
    this.fetchAllInFlight = null;

    return this.fetchAll();
  }

  public invalidate(): void {
    this.generation++;
    this.fetchAllInFlight = null;
    this.hasRequested = false;
  }

  public all(): Observable<PortfolioAccount[]> {
    return this.store.accounts$.pipe(
      distinctUntilChanged((prev, curr) => isEqual(prev, curr)),
      tap(() => {
        if (!this.hasRequested) {
          this.fetchAll();
        }
      }),
      filter(stored => !!stored)
    );
  }

  public syncAll(): PortfolioAccount[] {
    return this.store.syncAll();
  }

  public async checkIban(command: CheckIbanCommand): Promise<void> {
    const response = await this.http.post<HttpResponse>(
        `custodian/account/${command.id}/iban`, objKeysToSnakeCase(command), {},{observe: 'response'}
    );
    if (!isNoContent(response)) {
      return;
    }
    throw new ValidationError(ERROR_CODES.invalidCheckIban, 'shared.errors.ibanAlreadyConfigured');
  }

  public byCustomerPortfolioIdSync(customerPortfolioId: string): PortfolioAccount | null {
    const accounts = this.syncAll();
    return accounts?.find((account) => account.portfolioId === customerPortfolioId);
  }

  public byCustomerPortfolioId(customerPortfolioId: string): Observable<PortfolioAccount | null> {
    return this.all().pipe(
      map(accounts => accounts?.find((account) => account.portfolioId === customerPortfolioId))
    );
  }

  private async requestAll(): Promise<void> {
    const generation = this.generation;

    try {
      const data: PortfolioAccount[] = await this.http.get('custodian/account');

      if (generation !== this.generation) {
        return this.fetchAllInFlight ?? undefined;
      }

      this.store.saveAll(data);
    } catch (error) {
      if (generation === this.generation) {
        this.hasRequested = false;
      }

      throw error;
    }
  }
}
